/*
 * CheckoutDrawer — the booking checkout surface for Accommodation.
 *
 * Slides in from the right (design_guidelines: checkout_drawer). Holds the
 * stay being booked, a server-priced breakdown, the wallet picker, the
 * simulated card, and a Before / After balance ledger the participant sees
 * *before* committing.
 *
 * Authorisation is two-phase against the backend, mirroring a real card:
 *   POST /exercises/{id}/bookings          -> HOLD   (booking: pending)
 *   POST /bookings/{id}/confirm            -> CAPTURE (booking: confirmed)
 * Both run behind the single "Authorise" click so the participant experiences
 * one action, while the ledger records the two movements a controller expects
 * to see during the hot-wash.
 *
 * Every price shown here comes from the server quote. The drawer never
 * computes a total the backend hasn't already agreed to.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, CalendarBlank, Users, Bed, ShieldCheck, WarningCircle, CheckCircle, Spinner,
} from '@phosphor-icons/react';

import logisticsApi from '../../lib/logisticsApi';
import SimCard from './SimCard';

const fmt = (minor) =>
  ((minor || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const addDays = (isoDay, n) => {
  const d = new Date(`${isoDay}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

/** One line of the price breakdown. */
const Line = ({ label, value, strong, accent, testid }) => (
  <div className="flex items-baseline justify-between py-1.5" data-testid={testid}>
    <span
      className="text-sm"
      style={{ color: strong ? 'var(--ls-secondary)' : 'var(--ls-muted)', fontWeight: strong ? 600 : 400 }}
    >
      {label}
    </span>
    <span
      className="ls-mono"
      style={{
        fontSize: strong ? 16 : 13,
        fontWeight: strong ? 700 : 500,
        color: accent ? 'var(--ls-primary)' : 'var(--ls-secondary)',
      }}
    >
      Ÿ{fmt(value)}
    </span>
  </div>
);

const CheckoutDrawer = ({
  open,
  onClose,
  exerciseId,
  venue,
  wallets = [],
  currencySymbol = 'Ÿ',
  onBooked,
  roomType = null,
}) => {
  const [checkIn, setCheckIn] = useState(today());
  const [checkOut, setCheckOut] = useState(addDays(today(), 2));
  const [rooms, setRooms] = useState(1);
  const [guests, setGuests] = useState(1);

  const [walletId, setWalletId] = useState('');
  const [card, setCard] = useState(null);
  const [cvv, setCvv] = useState('');

  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(null);

  const wallet = useMemo(
    () => wallets.find((w) => w.wallet_id === walletId) || null,
    [wallets, walletId],
  );

  // Default to the wallet with the most headroom — usually what a participant
  // wants, and it stops the drawer opening onto a wallet that can't pay.
  useEffect(() => {
    if (walletId || wallets.length === 0) return;
    const best = [...wallets].sort((a, b) => (b.available || 0) - (a.available || 0))[0];
    setWalletId(best.wallet_id);
  }, [wallets, walletId]);

  // Reset transient state each time the drawer opens on a new venue.
  useEffect(() => {
    if (!open) return;
    setCvv('');
    setSubmitError(null);
    setSuccess(null);
  }, [open, venue?.venue_id]);

  // Pull the card face for the chosen wallet.
  useEffect(() => {
    if (!walletId) { setCard(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const c = await logisticsApi.walletSimCard(walletId);
        if (!cancelled) setCard(c);
      } catch {
        if (!cancelled) setCard(null);
      }
    })();
    return () => { cancelled = true; };
  }, [walletId]);

  // Re-quote whenever the stay changes. Debounced so dragging the room
  // stepper doesn't fire a request per click.
  const fetchQuote = useCallback(async () => {
    if (!open || !exerciseId || !venue?.venue_id) return;
    setQuoting(true);
    setQuoteError(null);
    try {
      const quoteBody = {
        venue_id: venue.venue_id,
        check_in: checkIn,
        check_out: checkOut,
        rooms: Number(rooms) || 1,
        guests: Number(guests) || 1,
      };
      if (roomType?.key) quoteBody.room_type = roomType.key;
      const q = await logisticsApi.quoteAccommodation(exerciseId, quoteBody);
      setQuote(q);
    } catch (e) {
      setQuote(null);
      setQuoteError(e.message || 'Could not price this stay');
    } finally {
      setQuoting(false);
    }
  }, [open, exerciseId, venue?.venue_id, checkIn, checkOut, rooms, guests, roomType?.key]);

  useEffect(() => {
    const t = setTimeout(fetchQuote, 250);
    return () => clearTimeout(t);
  }, [fetchQuote]);

  const total = quote?.total_minor || 0;
  const balanceBefore = wallet?.available || 0;
  const balanceAfter = balanceBefore - total;
  const canAfford = balanceAfter >= 0;
  const cvvReady = /^\d{3}$/.test(cvv);
  const ready = !!quote && !quoting && !!walletId && canAfford && cvvReady && !submitting;

  const authorise = async () => {
    if (!ready) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Phase 1 — hold. A stable key means a double-click or a retried
      // request can't place two holds for the same stay.
      const idem = `bkg:${exerciseId}:${venue.venue_id}:${checkIn}:${checkOut}:${rooms}:${walletId}`;
      const bookBody = {
        venue_id: venue.venue_id,
        check_in: checkIn,
        check_out: checkOut,
        rooms: Number(rooms) || 1,
        guests: Number(guests) || 1,
        wallet_id: walletId,
        cvv,
        idempotency_key: idem,
      };
      if (roomType?.key) bookBody.room_type = roomType.key;
      const held = await logisticsApi.createBooking(exerciseId, bookBody);
      // Phase 2 — capture.
      const confirmed = await logisticsApi.confirmBooking(held.booking.booking_id);
      setSuccess(confirmed.booking);
      setCvv('');
      onBooked?.(confirmed.booking, confirmed.wallet);
    } catch (e) {
      setSubmitError(e.message || 'Authorisation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
            data-testid="ls-checkout-scrim"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="ls-glass fixed top-0 right-0 h-full z-50 flex flex-col w-full sm:w-[460px]"
            style={{ borderLeft: '1px solid var(--ls-border)' }}
            data-testid="ls-checkout-drawer"
            role="dialog"
            aria-label="Checkout"
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 h-14 shrink-0"
              style={{ borderBottom: '1px solid var(--ls-border)' }}
            >
              <div className="flex items-center gap-2">
                <Bed size={16} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                <span className="ls-heading text-sm font-bold">Checkout</span>
              </div>
              <button
                onClick={onClose}
                className="ls-btn-ghost !p-2"
                aria-label="Close checkout"
                data-testid="ls-checkout-close"
              >
                <X size={14} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {success ? (
                <div className="text-center py-10" data-testid="ls-checkout-success">
                  <CheckCircle size={56} weight="duotone" style={{ color: 'var(--ls-success)' }} />
                  <h3 className="ls-heading text-xl font-bold mt-4">Booking confirmed</h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--ls-muted)' }}>
                    {success.venue_snapshot?.name}
                  </p>
                  <p className="ls-mono text-3xl font-bold mt-4" style={{ color: 'var(--ls-primary)' }}>
                    {currencySymbol}{fmt(success.total_minor)}
                  </p>
                  <p className="ls-mono text-[11px] mt-3" style={{ color: 'var(--ls-muted)' }}>
                    {success.booking_id}
                  </p>
                  <button className="ls-btn-primary mt-6" onClick={onClose} data-testid="ls-checkout-done">
                    Done
                  </button>
                </div>
              ) : (
                <>
                  {/* Venue header */}
                  <div className="flex gap-3 mb-5">
                    <div
                      className="w-20 h-20 shrink-0 rounded"
                      style={{
                        background: `url(${venue?.photo}) center / cover, var(--ls-elevated)`,
                        border: '1px solid var(--ls-border)',
                      }}
                    />
                    <div className="min-w-0">
                      <p className="ls-overline">{venue?.subcategory || 'stay'}</p>
                      <h3 className="ls-heading text-lg font-bold leading-tight truncate">
                        {venue?.name}
                      </h3>
                      {roomType && (
                        <p className="text-xs mt-0.5 font-semibold" style={{ color: 'var(--ls-primary)' }}>
                          {roomType.label} · {roomType.bed}
                        </p>
                      )}
                      {venue?.address && (
                        <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--ls-muted)' }}>
                          {venue.address}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Stay controls */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <label className="block">
                      <span className="ls-overline flex items-center gap-1 mb-1">
                        <CalendarBlank size={10} weight="bold" /> Check in
                      </span>
                      <input
                        type="date"
                        value={checkIn}
                        min={today()}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCheckIn(v);
                          if (v >= checkOut) setCheckOut(addDays(v, 1));
                        }}
                        className="ls-input w-full"
                        data-testid="ls-checkout-checkin"
                      />
                    </label>
                    <label className="block">
                      <span className="ls-overline flex items-center gap-1 mb-1">
                        <CalendarBlank size={10} weight="bold" /> Check out
                      </span>
                      <input
                        type="date"
                        value={checkOut}
                        min={addDays(checkIn, 1)}
                        onChange={(e) => setCheckOut(e.target.value)}
                        className="ls-input w-full"
                        data-testid="ls-checkout-checkout"
                      />
                    </label>
                    <label className="block">
                      <span className="ls-overline flex items-center gap-1 mb-1">
                        <Bed size={10} weight="bold" /> Rooms
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={rooms}
                        onChange={(e) => setRooms(e.target.value)}
                        className="ls-input w-full ls-mono"
                        data-testid="ls-checkout-rooms"
                      />
                    </label>
                    <label className="block">
                      <span className="ls-overline flex items-center gap-1 mb-1">
                        <Users size={10} weight="bold" /> Guests
                      </span>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={guests}
                        onChange={(e) => setGuests(e.target.value)}
                        className="ls-input w-full ls-mono"
                        data-testid="ls-checkout-guests"
                      />
                    </label>
                  </div>

                  {/* Price breakdown */}
                  <div className="ls-elevated p-4 mb-4" data-testid="ls-checkout-breakdown">
                    {quoting ? (
                      <div className="flex items-center gap-2 py-4 justify-center">
                        <Spinner size={14} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
                        <span className="text-sm" style={{ color: 'var(--ls-muted)' }}>Pricing…</span>
                      </div>
                    ) : quoteError ? (
                      <p className="text-sm py-2" style={{ color: 'var(--ls-danger)' }} data-testid="ls-checkout-quote-error">
                        {quoteError}
                      </p>
                    ) : quote ? (
                      <>
                        <Line
                          label={`Ÿ${fmt(quote.nightly_minor)} × ${quote.nights} night${quote.nights > 1 ? 's' : ''} × ${quote.rooms} room${quote.rooms > 1 ? 's' : ''}`}
                          value={quote.subtotal_minor}
                        />
                        <Line label="Simulated tax" value={quote.tax_minor} />
                        <Line label="Booking fee" value={quote.service_fee_minor} />
                        <div style={{ borderTop: '1px solid var(--ls-border)', marginTop: 6, paddingTop: 6 }}>
                          <Line label="Total" value={quote.total_minor} strong accent testid="ls-checkout-total" />
                        </div>
                        {quote.cancellation_policy?.summary && (
                          <p className="text-[11px] mt-2" style={{ color: 'var(--ls-muted)' }}>
                            {quote.cancellation_policy.summary}
                          </p>
                        )}
                      </>
                    ) : null}
                  </div>

                  {/* Wallet picker */}
                  <label className="block mb-4">
                    <span className="ls-overline mb-1 block">Pay from</span>
                    <select
                      value={walletId}
                      onChange={(e) => setWalletId(e.target.value)}
                      className="ls-input w-full"
                      data-testid="ls-checkout-wallet"
                    >
                      {wallets.length === 0 && <option value="">No wallet available</option>}
                      {wallets.map((w) => (
                        <option key={w.wallet_id} value={w.wallet_id}>
                          {w.owner_type === 'team' ? 'Team' : 'Personal'} · Ÿ{fmt(w.available)} available
                          {w.frozen ? ' · FROZEN' : ''}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Card + CVV */}
                  <div className="grid grid-cols-2 gap-3 mb-4 items-start">
                    <SimCard card={card} holder={card?.holder} compact />
                    <div>
                      <span className="ls-overline mb-1 block">Security code</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        value={cvv}
                        placeholder="•••"
                        onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 3))}
                        className="ls-input w-full ls-mono text-center"
                        style={{ fontSize: 20, letterSpacing: '0.35em' }}
                        data-testid="ls-checkout-cvv"
                        aria-label="Card security code"
                      />
                      <p className="text-[11px] mt-2 leading-snug" style={{ color: 'var(--ls-muted)' }}>
                        Read the code off your simulated card
                        {card?.cvv ? <> — <span className="ls-mono">{card.cvv}</span></> : null}.
                      </p>
                    </div>
                  </div>

                  {/* Before / After ledger */}
                  {wallet && quote && (
                    <div
                      className="ls-surface p-4"
                      data-testid="ls-checkout-ledger"
                      style={{ borderColor: canAfford ? 'var(--ls-border)' : 'var(--ls-danger)' }}
                    >
                      <p className="ls-overline mb-2">Balance</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[11px]" style={{ color: 'var(--ls-muted)' }}>Before</p>
                          <p className="ls-mono text-lg font-semibold">Ÿ{fmt(balanceBefore)}</p>
                        </div>
                        <span style={{ color: 'var(--ls-muted)' }}>→</span>
                        <div className="text-right">
                          <p className="text-[11px]" style={{ color: 'var(--ls-muted)' }}>After</p>
                          <p
                            className="ls-mono text-lg font-bold"
                            style={{ color: canAfford ? 'var(--ls-primary)' : 'var(--ls-danger)' }}
                            data-testid="ls-checkout-after"
                          >
                            Ÿ{fmt(balanceAfter)}
                          </p>
                        </div>
                      </div>
                      {!canAfford && (
                        <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: 'var(--ls-danger)' }}>
                          <WarningCircle size={13} weight="fill" />
                          Not enough available credit in this wallet.
                        </p>
                      )}
                    </div>
                  )}

                  {submitError && (
                    <p
                      className="text-sm mt-4 flex items-start gap-1.5"
                      style={{ color: 'var(--ls-danger)' }}
                      data-testid="ls-checkout-error"
                    >
                      <WarningCircle size={15} weight="fill" className="shrink-0 mt-0.5" />
                      {submitError}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Footer action */}
            {!success && (
              <div
                className="px-5 py-4 shrink-0"
                style={{ borderTop: '1px solid var(--ls-border)' }}
              >
                <button
                  className="ls-btn-primary w-full justify-center"
                  disabled={!ready}
                  onClick={authorise}
                  data-testid="ls-checkout-authorise"
                >
                  {submitting ? (
                    <><Spinner size={15} className="animate-spin" /> Authorising…</>
                  ) : (
                    <><ShieldCheck size={15} weight="fill" /> Authorise Ÿ{fmt(total)}</>
                  )}
                </button>
                <p className="text-[11px] text-center mt-2" style={{ color: 'var(--ls-muted)' }}>
                  Funds are held, then captured on confirmation.
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CheckoutDrawer;
